import { useMemo, useState } from "react";
import type { RevenueReport } from "@codestack/shared";
import { Button, Input, Select } from "@codestack/ui";
import { useApi } from "../lib/useApi";
import { EmptyState, ErrorState, LoadingState } from "../components/States";
import { money, percent } from "../lib/format";

type GroupBy = "day" | "month" | "platform";

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function RevenuePage() {
  const [groupBy, setGroupBy] = useState<GroupBy>("month");
  const [from, setFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    date.setDate(1);
    return isoDate(date);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));

  const query = useMemo(
    () => ({ groupBy, from: from || undefined, to: to || undefined }),
    [groupBy, from, to],
  );

  const { data, error, loading, reload } = useApi<RevenueReport>("/admin/reports/revenue", query);

  const maxGross = useMemo(() => {
    if (!data) return 0;
    return data.points.reduce((max, point) => Math.max(max, Number(point.grossMinor)), 0);
  }, [data]);

  const feeShare = useMemo(() => {
    if (!data) return 0;
    const gross = Number(data.totalGrossMinor);
    if (gross <= 0) return 0;
    return (Number(data.totalFeesMinor) / gross) * 100;
  }, [data]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-bold text-white">Revenue</h1>
        <p className="text-sm text-ink-400">
          Gross volume, platform fees and net revenue across the gateway.
        </p>
      </div>

      <div className="admin-card grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-[200px_1fr_1fr_auto]">
        <Select
          label="Group by"
          value={groupBy}
          onChange={(event) => setGroupBy(event.target.value as GroupBy)}
        >
          <option value="day">Day</option>
          <option value="month">Month</option>
          <option value="platform">Platform</option>
        </Select>
        <Input
          label="From"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <Input
          label="To"
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
        <div className="flex items-end">
          <Button variant="secondary" onClick={reload} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {loading && !data ? <LoadingState label="Calculating revenue…" /> : null}

      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="admin-card p-5">
              <p className="admin-label">Gross volume</p>
              <p className="admin-value mt-2">
                {money(data.totalGrossMinor, data.currency)}
              </p>
            </div>
            <div className="admin-card p-5">
              <p className="admin-label">Fees collected</p>
              <p className="admin-value mt-2">
                {money(data.totalFeesMinor, data.currency)}
              </p>
              <p className="mt-1 text-[11px] text-ink-500">
                {percent(feeShare, 2)} of gross
              </p>
            </div>
            <div className="admin-card p-5">
              <p className="admin-label">Net revenue</p>
              <p className="admin-value mt-2">{money(data.totalNetMinor, data.currency)}</p>
            </div>
            <div className="admin-card p-5">
              <p className="admin-label">Transactions</p>
              <p className="admin-value mt-2">{data.totalTransactions}</p>
            </div>
          </div>

          {data.points.length === 0 ? (
            <EmptyState
              title="No settled payments in this range"
              description="Adjust the date range or wait for payments to be verified."
            />
          ) : (
            <section className="admin-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-sm font-semibold text-white">
                  Volume by {groupBy}
                </h2>
                <p className="text-[11px] text-ink-500">
                  {data.points.length} period{data.points.length === 1 ? "" : "s"}
                </p>
              </div>

              <div className="mt-5 space-y-2.5">
                {data.points.map((point) => {
                  const width = maxGross > 0 ? (Number(point.grossMinor) / maxGross) * 100 : 0;
                  return (
                    <div key={point.period} className="grid grid-cols-[110px_1fr_auto] items-center gap-3">
                      <span className="truncate font-mono text-[11px] text-ink-400">
                        {point.period}
                      </span>
                      <div className="h-2 overflow-hidden rounded-full bg-white/[0.05]">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-400"
                          style={{ width: `${Math.max(width, 1)}%` }}
                        />
                      </div>
                      <span className="w-28 text-right font-mono text-[11px] text-ink-200">
                        {money(point.grossMinor, data.currency)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="table-shell">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Platform</th>
                    <th className="px-4 py-3 text-right font-semibold">Gross</th>
                    <th className="px-4 py-3 text-right font-semibold">Fees</th>
                    <th className="px-4 py-3 text-right font-semibold">Net</th>
                    <th className="px-4 py-3 text-right font-semibold">Txns</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {data.byPlatform.map((row) => (
                    <tr key={row.platformId} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-xs text-ink-200">{row.platformName}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-100">
                        {money(row.grossMinor, data.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-amber-200/80">
                        {money(row.feesMinor, data.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-emerald-200/90">
                        {money(row.netMinor, data.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-400">
                        {row.transactionCount}
                      </td>
                    </tr>
                  ))}
                  {data.byPlatform.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-xs text-ink-500">
                        No platform revenue in this range.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
